import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeftIcon, CopyIcon, SaveIcon, Trash2Icon, UserCheckIcon } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { PROSPECTING_KEY, prospectingApi } from '@/api/prospecting'
import type { Id, Prospect, ProspectStatus, ProspectTier } from '@/api/types'
import { useAuth } from '@/auth/use-auth'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { FieldError, FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { MarkdownEditor } from '@/components/markdown-editor'
import { PageHeader } from '@/components/page-header'
import { ConvertProspectDialog } from '@/components/prospecting/convert-prospect-dialog'
import { ProspectTierBadge } from '@/components/prospecting/prospect-tier-badge'
import { ReferencedBy } from '@/components/references/referenced-by'
import { ReturnLink } from '@/components/return-link'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { UserPicker } from '@/components/user-picker'
import { formatDateOnly, formatDateTime } from '@/lib/format'
import { authorName } from '@/lib/people'
import {
  PROSPECT_SOURCE_LABELS,
  PROSPECT_STATUS_LABELS,
  PROSPECT_STATUSES,
  PROSPECT_TIER_LABELS,
  PROSPECT_TIERS,
} from '@/lib/prospecting'
import { RETURN_SHAPE } from '@/lib/return-to'
import { readSearch } from '@/lib/search-params'

export const Route = createFileRoute('/_app/prospecting/prospects/$prospectId')({
  // #401 : « Retour » ramène à la liste filtrée d'où l'on vient.
  validateSearch: (search: Record<string, unknown>) => readSearch(RETURN_SHAPE, search),
  component: ProspectPage,
})

const KEEP = '__grille__'

/**
 * La fiche d'un prospect (lot 43, contrat 1.86.0) : qui il est, ce qu'il a
 * répondu — **sa parole**, rendue comme texte venu du dehors —, pourquoi ce
 * score, la classe et sa correction (note obligatoire), le suivi, d'où il
 * vient, son consentement, puis « Convertir ». Effacer (Loi 25) est un geste
 * d'administrateur, définitif.
 */
function ProspectPage() {
  const { prospectId } = Route.useParams()
  const prospect = useQuery({
    queryKey: [...PROSPECTING_KEY, 'prospect', prospectId],
    queryFn: () => prospectingApi.prospect(prospectId),
    retry: false,
  })
  if (prospect.isPending) {
    return (
      <Frame>
        <Skeleton className="h-64 w-full" />
      </Frame>
    )
  }
  if (prospect.isError) {
    return (
      <Frame>
        <FormError>
          {prospect.error instanceof ApiError && prospect.error.isNotFound
            ? 'Ce prospect n’existe pas, ou plus : il a pu être effacé à échéance de conservation.'
            : 'Le prospect n’a pas pu être lu.'}
        </FormError>
      </Frame>
    )
  }
  return <ProspectDetail prospect={prospect.data} />
}

function ProspectDetail({ prospect }: { prospect: Prospect }) {
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [converting, setConverting] = useState(false)
  const [erasing, setErasing] = useState(false)
  const erase = useMutation({
    mutationFn: () => prospectingApi.remove(prospect.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROSPECTING_KEY })
      toast.success('Prospect effacé définitivement.')
      void navigate({ to: '/prospecting' })
    },
  })
  const name = prospect.full_name ?? prospect.email ?? prospect.short_id

  return (
    <div className="space-y-6" data-testid="fiche-prospect">
      <ReturnLink
        className="-ml-2 self-start"
        fallback={
          <Link
            to="/prospecting"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeftIcon className="size-4" aria-hidden="true" />
            Prospection
          </Link>
        }
      />
      <PageHeader
        title={name}
        description={
          [prospect.company, prospect.job_title].filter(Boolean).join(' · ') || undefined
        }
        actions={
          prospect.converted ? undefined : (
            <Button
              data-testid="convertir"
              onClick={() => {
                setConverting(true)
              }}
            >
              <UserCheckIcon />
              Convertir
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <ProspectTierBadge tier={prospect.tier} corrected={prospect.tier_override !== null} />
        <span className="tabular-nums">Score {prospect.score}</span>
        <span className="text-muted-foreground">·</span>
        <Link
          to="/prospecting/programs/$programId"
          params={{ programId: prospect.program.short_id }}
          className="text-primary underline-offset-2 hover:underline"
        >
          {prospect.program.name}
        </Link>
        <span className="text-muted-foreground">
          · {PROSPECT_SOURCE_LABELS[prospect.source]} · reçu le{' '}
          {formatDateTime(prospect.received_at)}
        </span>
      </div>

      {prospect.converted && (
        <p
          className="rounded-md border border-success/40 bg-success/5 p-3 text-sm"
          data-testid="prospect-converti"
        >
          Converti le {formatDateTime(prospect.converted.at)}
          {prospect.converted.by ? ` par ${authorName(prospect.converted.by)}` : ''} :{' '}
          <Link
            to="/clients/$clientId"
            params={{ clientId: prospect.converted.client_id }}
            className="text-primary underline-offset-2 hover:underline"
          >
            le client
          </Link>
          {prospect.converted.opportunity_id && (
            <>
              {', '}
              <Link
                to="/opportunities/$opportunityId"
                params={{ opportunityId: prospect.converted.opportunity_id }}
                className="text-primary underline-offset-2 hover:underline"
                data-testid="lien-affaire"
              >
                l’affaire
              </Link>
            </>
          )}
          .
        </p>
      )}
      {(prospect.duplicate_of_id ?? prospect.matching_contact) && (
        <div
          className="space-y-1 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm"
          data-testid="doublon"
        >
          {prospect.duplicate_of_id && (
            <p className="flex items-center gap-1.5">
              <CopyIcon className="size-4" aria-hidden="true" />
              Même adresse ou même téléphone qu’un prospect plus ancien :{' '}
              <Link
                to="/prospecting/prospects/$prospectId"
                params={{ prospectId: prospect.duplicate_of_id }}
                className="text-primary underline-offset-2 hover:underline"
              >
                le voir
              </Link>
              . Une deuxième réponse est un signal ; rien n’a été fusionné.
            </p>
          )}
          {prospect.matching_contact && (
            <p>
              Déjà au CRM : {prospect.matching_contact.full_name}, contact de{' '}
              <Link
                to="/clients/$clientId"
                params={{ clientId: prospect.matching_contact.client_id }}
                className="text-primary underline-offset-2 hover:underline"
              >
                {prospect.matching_contact.client_name}
              </Link>
              . La conversion propose ce client.
            </p>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Frame className="space-y-4">
          <section className="space-y-2" aria-label="Identité">
            <h2 className="font-semibold tracking-tight">Identité</h2>
            <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-sm">
              {(
                [
                  ['Nom', prospect.full_name],
                  ['Courriel', prospect.email],
                  ['Téléphone', prospect.phone],
                  ['Entreprise', prospect.company],
                  ['Fonction', prospect.job_title],
                  ['Site', prospect.website],
                  ['Ville', prospect.city],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="contents">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="min-w-0 break-words">{value ?? '—'}</dd>
                </div>
              ))}
            </dl>
          </section>
          <Answers prospect={prospect} />
        </Frame>
        <Frame className="space-y-4">
          <WhyThisScore prospect={prospect} />
          <TierCorrection prospect={prospect} />
        </Frame>
      </div>

      <Frame>
        <FollowUp prospect={prospect} />
      </Frame>

      <div className="grid gap-6 lg:grid-cols-2">
        <Frame>
          <Attribution prospect={prospect} />
        </Frame>
        <Frame className="space-y-3 text-sm">
          <h2 className="font-semibold tracking-tight">Consentement aux messages</h2>
          <p data-testid="consentement">
            {prospect.consent.messages
              ? `Donné${prospect.consent.at ? ` le ${formatDateTime(prospect.consent.at)}` : ''}.`
              : 'Non donné : ne lui envoyez pas de messages commerciaux (LCAP).'}
          </p>
          {prospect.consent.text && (
            <blockquote className="border-l-2 pl-3 text-muted-foreground">
              {prospect.consent.text}
            </blockquote>
          )}
          <p className="text-muted-foreground">
            {prospect.purge_after
              ? `Effacé le ${formatDateOnly(prospect.purge_after)} s’il n’est pas converti d’ici là.`
              : 'Converti : il n’est plus effacé à échéance.'}
          </p>
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setErasing(true)
              }}
            >
              <Trash2Icon />
              Effacer définitivement
            </Button>
          )}
          {erase.error && (
            <FormError>
              {erase.error instanceof ApiError
                ? erase.error.message
                : 'L’effacement n’a pas abouti.'}
            </FormError>
          )}
        </Frame>
      </div>

      <ReferencedBy type="prospect" id={prospect.id} />

      <ConvertProspectDialog prospect={prospect} open={converting} onOpenChange={setConverting} />
      <ConfirmDialog
        open={erasing}
        onOpenChange={setErasing}
        onConfirm={() => {
          erase.mutate()
        }}
        title={`Effacer ${name} définitivement ?`}
        description="Le droit à l’effacement (Loi 25) : ses réponses et son identité disparaissent sans retour. Le journal ne garde que la trace du geste."
        confirmLabel="Effacer définitivement"
        destructive
        pending={erase.isPending}
      />
    </div>
  )
}

/** Ses réponses : la parole d'une personne extérieure, jamais une consigne. */
function Answers({ prospect }: { prospect: Prospect }) {
  if (prospect.answers.length === 0) {
    return <p className="text-sm text-muted-foreground">Aucune réponse aux questions.</p>
  }
  // Un choix arrive par sa clé d'option ; on montre le libellé que le prospect a vu,
  // tel que la ligne du score le nomme. Une réponse libre se lit telle quelle.
  const chosen = new Map(prospect.score_lines.map((line) => [line.question_key, line.answer]))
  return (
    <section className="space-y-2" aria-label="Réponses" data-testid="reponses-prospect">
      <h2 className="font-semibold tracking-tight">Ses réponses</h2>
      <p className="text-xs text-muted-foreground">
        Texte venu du prospect, tel qu’il l’a écrit ou choisi.
      </p>
      <ul className="space-y-2 text-sm">
        {prospect.answers.map((answer, index) => (
          <li key={`${answer.question_key ?? 'libre'}-${String(index)}`} className="space-y-0.5">
            <p className="text-muted-foreground">
              {answer.label}
              {answer.question_key === null && (
                <Badge variant="outline" className="ml-2">
                  sans correspondance
                </Badge>
              )}
            </p>
            <blockquote className="border-l-2 pl-3 whitespace-pre-wrap">
              {(answer.question_key === null ? undefined : chosen.get(answer.question_key)) ??
                answer.value}
            </blockquote>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** « Pourquoi ce score » : d'où vient chaque point, et quel motif exclut. */
function WhyThisScore({ prospect }: { prospect: Prospect }) {
  const total = prospect.score_lines.reduce((sum, line) => sum + line.points, 0)
  return (
    <section className="space-y-2" aria-label="Pourquoi ce score" data-testid="pourquoi-score">
      <h2 className="font-semibold tracking-tight">Pourquoi ce score</h2>
      {prospect.score_lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Aucune réponse ne rapporte de points : le score est 0.
        </p>
      ) : (
        <table className="w-full text-sm">
          <tbody className="divide-y">
            {prospect.score_lines.map((line) => (
              <tr key={line.question_key} data-testid="ligne-score">
                <td className="py-1.5 pr-2 text-muted-foreground">{line.label}</td>
                <td className="py-1.5 pr-2">{line.answer}</td>
                <td className="py-1.5 text-right tabular-nums">
                  {line.disqualifies ? (
                    <Badge variant="destructive">exclut</Badge>
                  ) : (
                    `${line.points > 0 ? '+' : ''}${String(line.points)}`
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t font-medium">
              <td className="py-1.5" colSpan={2}>
                Total{total !== prospect.score ? ` ${String(total)}, ramené entre 0 et 100` : ''}
              </td>
              <td className="py-1.5 text-right tabular-nums">{prospect.score}</td>
            </tr>
          </tfoot>
        </table>
      )}
      <p className="text-sm">
        Classe selon la grille : <ProspectTierBadge tier={prospect.tier_computed} />
      </p>
    </section>
  )
}

/** Corriger la classe : une note est exigée, et la correction se voit partout. */
function TierCorrection({ prospect }: { prospect: Prospect }) {
  const ids = { tier: useId(), note: useId() }
  const queryClient = useQueryClient()
  const [tier, setTier] = useState<string>(prospect.tier_override ?? KEEP)
  const [note, setNote] = useState(prospect.tier_override_note ?? '')
  const [touched, setTouched] = useState(false)
  useEffect(() => {
    setTier(prospect.tier_override ?? KEEP)
    setNote(prospect.tier_override_note ?? '')
  }, [prospect.tier_override, prospect.tier_override_note])
  const save = useMutation({
    mutationFn: () =>
      prospectingApi.updateProspect(prospect.id, {
        tier_override: tier === KEEP ? null : (tier as ProspectTier),
        tier_override_note: tier === KEEP ? null : note.trim(),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData([...PROSPECTING_KEY, 'prospect', prospect.short_id], saved)
      void queryClient.invalidateQueries({ queryKey: PROSPECTING_KEY })
      toast.success(saved.tier_override ? 'Classe corrigée.' : 'Classe de la grille rétablie.')
    },
  })
  const noteMissing = tier !== KEEP && note.trim() === ''
  const changed =
    tier !== (prospect.tier_override ?? KEEP) || note !== (prospect.tier_override_note ?? '')
  return (
    <section
      className="space-y-2 border-t pt-4"
      aria-label="Corriger la classe"
      data-testid="corriger-classe"
    >
      <h3 className="text-sm font-medium">Corriger la classe</h3>
      {prospect.tier_override && (
        <p className="text-sm text-muted-foreground">
          Corrigée en « {PROSPECT_TIER_LABELS[prospect.tier_override]} » :{' '}
          {prospect.tier_override_note}
        </p>
      )}
      <div className="grid gap-2 sm:grid-cols-[12rem_1fr]">
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger id={ids.tier} aria-label="Classe">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={KEEP}>La classe de la grille</SelectItem>
            {PROSPECT_TIERS.map((item) => (
              <SelectItem key={item} value={item}>
                {PROSPECT_TIER_LABELS[item]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          id={ids.note}
          aria-label="Pourquoi cette correction"
          placeholder="Pourquoi (obligatoire)"
          value={note}
          disabled={tier === KEEP}
          maxLength={500}
          onChange={(event) => {
            setNote(event.target.value)
          }}
        />
      </div>
      {touched && noteMissing && (
        <FieldError>Une correction se justifie : écrivez pourquoi.</FieldError>
      )}
      {save.error && (
        <FormError>
          {save.error instanceof ApiError && save.error.reason === 'override_note_required'
            ? 'Une correction se justifie : écrivez pourquoi.'
            : 'La correction n’a pas abouti.'}
        </FormError>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!changed || save.isPending}
        data-testid="corriger-classe-enregistrer"
        onClick={() => {
          setTouched(true)
          if (!noteMissing) save.mutate()
        }}
      >
        Enregistrer la classe
      </Button>
    </section>
  )
}

/** Le suivi : statut, responsable, prochaine action, notes de l'équipe. */
function FollowUp({ prospect }: { prospect: Prospect }) {
  const ids = { status: useId(), owner: useId(), next: useId(), notes: useId() }
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<ProspectStatus>(prospect.status)
  const [owner, setOwner] = useState<Id | null>(prospect.owner_id)
  const [next, setNext] = useState(prospect.next_action_at ?? '')
  const [notes, setNotes] = useState(prospect.notes ?? '')
  useEffect(() => {
    setStatus(prospect.status)
    setOwner(prospect.owner_id)
    setNext(prospect.next_action_at ?? '')
    setNotes(prospect.notes ?? '')
  }, [prospect])
  const save = useMutation({
    mutationFn: () =>
      prospectingApi.updateProspect(prospect.id, {
        status,
        owner_id: owner,
        next_action_at: next || null,
        notes: notes.trim() || null,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData([...PROSPECTING_KEY, 'prospect', prospect.short_id], saved)
      void queryClient.invalidateQueries({ queryKey: PROSPECTING_KEY })
      toast.success('Suivi enregistré.')
    },
  })
  const dirty =
    status !== prospect.status ||
    owner !== prospect.owner_id ||
    next !== (prospect.next_action_at ?? '') ||
    notes !== (prospect.notes ?? '')
  return (
    <section className="space-y-3" aria-label="Suivi" data-testid="suivi-prospect">
      <h2 className="font-semibold tracking-tight">Suivi</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor={ids.status}>Statut</Label>
          <Select
            value={status}
            onValueChange={(value) => {
              setStatus(value as ProspectStatus)
            }}
          >
            <SelectTrigger id={ids.status} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROSPECT_STATUSES.filter(
                (item) => item !== 'converted' || prospect.status === 'converted',
              ).map((item) => (
                <SelectItem key={item} value={item}>
                  {PROSPECT_STATUS_LABELS[item]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor={ids.owner}>Responsable</Label>
          <UserPicker id={ids.owner} value={owner} onChange={setOwner} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={ids.next}>Prochaine action le</Label>
          <Input
            id={ids.next}
            type="date"
            value={next}
            onChange={(event) => {
              setNext(event.target.value)
            }}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor={ids.notes}>Notes de l’équipe</Label>
        <MarkdownEditor id={ids.notes} rows={4} value={notes} onChange={setNotes} />
      </div>
      {save.error && (
        <FormError>
          {save.error instanceof ApiError ? save.error.message : 'Enregistrement impossible.'}
        </FormError>
      )}
      <div className="flex justify-end">
        <Button
          type="button"
          disabled={!dirty || save.isPending}
          data-testid="suivi-enregistrer"
          onClick={() => {
            save.mutate()
          }}
        >
          <SaveIcon />
          Enregistrer le suivi
        </Button>
      </div>
    </section>
  )
}

/** D'où il vient : campagne, publicité, plateforme, UTM — jamais deviné. */
function Attribution({ prospect }: { prospect: Prospect }) {
  const a = prospect.attribution
  const rows: [string, string | null][] = [
    ['Campagne', a.meta_campaign_name ?? a.meta_campaign_id],
    ['Publicité', a.meta_ad_name ?? a.meta_ad_id],
    [
      'Plateforme',
      a.platform === 'instagram' ? 'Instagram' : a.platform === 'facebook' ? 'Facebook' : null,
    ],
    ['Organique', a.is_organic === null ? null : a.is_organic ? 'oui' : 'non (publicité payée)'],
    ['utm_source', a.utm_source],
    ['utm_medium', a.utm_medium],
    ['utm_campaign', a.utm_campaign],
    ['utm_content', a.utm_content],
    ['utm_term', a.utm_term],
    ['fbclid', a.fbclid],
    ['Page', a.landing_url],
    ['Provenance', a.referrer],
  ]
  const shown = rows.filter(([, value]) => value)
  return (
    <section className="space-y-2 text-sm" aria-label="Attribution" data-testid="attribution">
      <h2 className="font-semibold tracking-tight">Attribution</h2>
      {shown.length === 0 ? (
        <p className="text-muted-foreground">Aucune trace de provenance : saisi par l’équipe.</p>
      ) : (
        <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1">
          {shown.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="min-w-0 break-all">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  )
}
